/* eslint-disable @typescript-eslint/no-explicit-any */
// src/providers.tsx
'use client';

import React from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { store } from './store';

import { setAuthHintCookie } from '@/utils/auth';
import { logout, setTokenFromStorage, setUser } from './auth/login/slice';

function AuthBootstrap() {
  const dispatch = useDispatch();
  const bootedRef = React.useRef(false);
  const login = useSelector((s: any) => s.login);

  // 1) One-time hydration from localStorage (tokens + userProfile)
  React.useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;

    try {
      const access = localStorage.getItem('accessToken') || '';
      const refresh = localStorage.getItem('refreshToken') || '';
      const rawProfile = localStorage.getItem('userProfile');

      if (access || refresh) {
        // hydrate token slice
        dispatch(setTokenFromStorage({ access, refresh } as any));
        // fast UI hint (no secrets)
        setAuthHintCookie(!!access);
      } else {
        setAuthHintCookie(false);
      }

      // hydrate user slice if profile exists
      if (rawProfile && !login?.user) {
        try {
          const parsed = JSON.parse(rawProfile);
          if (parsed && typeof parsed === 'object') {
            dispatch(setUser(parsed));
          }
        } catch {
          // bad JSON → clear it
          localStorage.removeItem('userProfile');
        }
      }
    } catch {
      // localStorage unavailable or errored → ensure logged out state
      dispatch(logout());
      setAuthHintCookie(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // 2) Keep the hint cookie in sync if token changes later
  React.useEffect(() => {
    try {
      setAuthHintCookie(!!login?.token?.access);
    } catch {
      /* ignore */
    }
  }, [login?.token?.access]);

  // 3) Optional: react to cross-tab logins/logouts (storage events)
  React.useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'accessToken' || e.key === 'refreshToken') {
        const access = localStorage.getItem('accessToken') || '';
        const refresh = localStorage.getItem('refreshToken') || '';
        if (access || refresh) {
          dispatch(setTokenFromStorage({ access, refresh } as any));
          setAuthHintCookie(!!access);
        } else {
          dispatch(logout());
          setAuthHintCookie(false);
        }
      }
      if (e.key === 'userProfile') {
        const raw = localStorage.getItem('userProfile');
        if (raw) {
          try {
            dispatch(setUser(JSON.parse(raw)));
          } catch {
            localStorage.removeItem('userProfile');
          }
        } else {
          // cleared elsewhere
          dispatch(setUser(null as any));
        }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [dispatch]);

  return null; // no UI
}

export function Providers({ children }: { children: React.ReactNode }) {
  return (
    <Provider store={store}>
      <AuthBootstrap />
      {children}
    </Provider>
  );
}
